import { AnimatePresence, motion } from 'framer-motion';
import { Newspaper } from 'lucide-react';
import { useEffect, useState } from 'react';
import { usePreviewImage } from '../../hooks/usePreviewImage';
import { useNews, type NewsItem } from '../../hooks/useWidgetsData';
import { useSettings } from '../../store/settings';
import { timeAgo, useArticle } from '../ArticleModal';

/** One story at a time – picture, headline, summary – rotating every few seconds. compact uses a letterbox picture for narrow columns. */
export function NewsCard({ compact = false }: { compact?: boolean }) {
  const interval = useSettings((s) => s.news.cardIntervalSec);
  const open = useArticle((s) => s.open);
  const { data, loading } = useNews();
  const [index, setIndex] = useState(0);
  const items = data ?? [];

  useEffect(() => {
    if (items.length < 2) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % items.length), interval * 1000);
    return () => window.clearInterval(t);
  }, [items.length, interval]);

  if (!items.length) {
    return <div className="glass muted p-4 text-sm">{loading ? 'Loading news…' : 'No news right now.'}</div>;
  }
  const item = items[index % items.length]!;
  return <Card item={item} index={index} total={items.length} interval={interval} compact={compact} onOpen={() => open(item)} />;
}

function Card({
  item,
  index,
  total,
  interval,
  compact,
  onOpen,
}: {
  item: NewsItem;
  index: number;
  total: number;
  interval: number;
  compact: boolean;
  onOpen: () => void;
}) {
  const image = usePreviewImage(item.image, item.link);

  return (
    <button
      onClick={onOpen}
      className="glass block w-full overflow-hidden text-left transition hover:brightness-110"
      aria-label={`Open story: ${item.title}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={`${index}-${item.link ?? item.title}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
          {image && (
            <img src={image} alt="" className={`w-full object-cover ${compact ? 'aspect-[21/9]' : 'aspect-[16/9]'}`} loading="lazy" onError={(e) => (e.currentTarget.style.display = 'none')} />
          )}
          <div className="space-y-1.5 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">
              <Newspaper className="h-3.5 w-3.5" aria-hidden /> {item.source}
              <span className="muted font-normal normal-case">{timeAgo(item.published)}</span>
            </div>
            <div className="font-heading text-lg font-medium leading-snug">{item.title}</div>
            {item.summary && <p className="muted line-clamp-3 text-sm">{item.summary}</p>}
          </div>
        </motion.div>
      </AnimatePresence>
      {total > 1 && (
        <div className="h-0.5 bg-[var(--border)]">
          <motion.div
            key={index}
            className="h-full bg-[var(--accent)]"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: interval, ease: 'linear' }}
          />
        </div>
      )}
    </button>
  );
}
